import {OrderChangeRequestStatus} from '@hconnect/tuc-processes/orderManagement'
import {useMemo} from 'react'

import {useHubQuery} from '../../QueryProvider'

/**
 * Hook to fetch failed order change requests from the API.
 *
 * @param startDate - The start date to filter requests (ISO date string, e.g., '2026-01-29')
 * @param enabled - Whether the query should be enabled
 * @returns Query result with failed order change requests filtered by dismissed ones
 */
export const useGetFailedOrderChangeRequests = (startDate: string | undefined, enabled = true) => {
  // Fetch all failed order change requests
  const {
    data: failedRequests,
    isLoading: isLoadingRequests,
    ...rest
  } = useHubQuery('failedOrderChangeRequests', [startDate ?? ''], {
    enabled: enabled && !!startDate
  })

  // Fetch dismissed request IDs from distributed user storage
  const {data: dismissedRequestIds = [], isLoading: isLoadingDismissed} = useHubQuery(
    'dismissedFailedOrderChangeRequests',
    [],
    {
      enabled: enabled && !!startDate
    }
  )

  // Filter out dismissed requests
  const filteredRequests = useMemo<OrderChangeRequestStatus[]>(() => {
    if (!failedRequests) return []
    return failedRequests.filter((request) => !dismissedRequestIds.includes(request.requestId))
  }, [failedRequests, dismissedRequestIds])

  return {
    data: filteredRequests,
    isLoading: isLoadingRequests || isLoadingDismissed,
    ...rest
  }
}
